import React, { FC } from 'react'
import styled, { css } from 'styled-components'
import { Flex } from '@/components/atoms/Grid'
import { Capitalized, Capitalized2, Tiny } from '@/components/atoms/typography'
import useTranslation from 'next-translate/useTranslation'
import { transparentize } from 'polished'
import { useTheme } from 'styled-components'
import { Currency, PromoCode } from '@/network/graphql.g'
import { DayDeliveryIconDefault } from '../atoms/icons/DayDeliveryIconDefault'
import { validFor } from '@/theme/validFor'
import { useAsset } from '@/providers/assetsProvider'
import { ReturnIconPlainDefault } from '@/components/atoms/icons/ReturnPlainIconDefault'
import { FreeShippingBigIconDefault } from '@/components/atoms/icons/FreeShippingBigIconDefault'

type Voucher = PromoCode & {
  currency: Currency
  content?: JSX.Element
}

type ReturnPolicy = {
  moreInfoUrl: string
}

enum HighlightType {
  None,
  Green,
  Accent
}

type FreeShipping = {
  freeShippingInfo: string | React.ReactNode
  highlightType?: HighlightType
  big?: boolean
}

type PurchasePromoInfo = Voucher | ReturnPolicy | FreeShipping

type PurchasePromoInfoBoxProps = {
  info: PurchasePromoInfo
  canRevertProduct?: boolean
  cartLayout?: boolean
} & Rest

const PurchasePromoInfoBox: FC<PurchasePromoInfoBoxProps> = ({
  info,
  canRevertProduct,
  ...rest
}) => {
  const { t } = useTranslation('common')
  const { isRsAndBaMarket } = useAsset()
  const theme = useTheme()

  const showReturnPolicy = canRevertProduct ? true : isRsAndBaMarket
  if ('moreInfoUrl' in info && showReturnPolicy) {
    return (
      <GreyWrapper {...rest}>
        <ReturnIconPlainDefault
          color={theme.colors.accent}
          width={20}
          height={20}
        />
        <StyledReturnTitle>
          {canRevertProduct
            ? t('ProductDetail.Promo.returnPolicy')
            : t('ProductDetail.Promo.underwearReturnPolicy')}
        </StyledReturnTitle>
      </GreyWrapper>
    )
  } else if ('freeShippingInfo' in info) {
    const freeShipping = info as FreeShipping
    const body = (
      <>
        <FreeShippingBigIconDefault
          color={!rest.cartLayout && theme.colors.accent}
          width={!rest.cartLayout && 20}
          height={!rest.cartLayout && 20}
        />
        {freeShipping.big ? (
          <StyleCapitalized2
            ml={rest.cartLayout ? 15 : 22}
            my={rest.cartLayout ? { desktop: 11, tablet: 10, mobile: 10 } : 20}
            cartLayout={rest.cartLayout}
          >
            {freeShipping.freeShippingInfo}
          </StyleCapitalized2>
        ) : (
          <StyleCapitalized
            ml={rest.cartLayout ? 15 : 18}
            mt={
              rest.cartLayout ? { desktop: 11, tablet: 10, mobile: 10 } : '4px'
            }
            mb={
              rest.cartLayout ? { desktop: 11, tablet: 10, mobile: 10 } : '4px'
            }
          >
            {freeShipping.freeShippingInfo}
          </StyleCapitalized>
        )}
      </>
    )

    switch (freeShipping.highlightType) {
      case HighlightType.Green:
        return <GreenWrapper {...rest}>{body}</GreenWrapper>
      case HighlightType.Accent:
        return <AccentWrapper {...rest}>{body}</AccentWrapper>
      default:
        return <GreyWrapper {...rest}>{body}</GreyWrapper>
    }
  }
  return null
}

type ShippingTimePromoInfoBoxProps = {
  shippingTitle: string
} & Rest

export const ShippingTimePromoInfoBox: FC<ShippingTimePromoInfoBoxProps> = ({
  shippingTitle,
  ...rest
}) => {
  const theme = useTheme()
  return (
    <AccentWrapper p="3px 6px !important" {...rest}>
      <StyledFlex>
        <DayDeliveryIconDefault color={theme.colors.accent} />
        <StyledTiny my={0} ml="5px" color={theme.colors.accent}>
          {shippingTitle}
        </StyledTiny>
      </StyledFlex>
    </AccentWrapper>
  )
}

export default PurchasePromoInfoBox

const StyleCapitalized2 = styled(Capitalized2)`
  font-weight: ${(props) => !props.cartLayout && '400'};
  ${validFor.mobile` 
    font-size: 14px;
	`};
`

const StyleCapitalized = styled(Capitalized)`
  font-weight: 400;
`

const WrapperBase = css`
  flex-direction: row;
  padding: 4px 20px;
  border-radius: ${(props) => props.theme.borderRadius}px;
`

const GreyWrapper = styled(Flex)`
  ${WrapperBase};
`

const GreenWrapper = styled(Flex)`
  ${WrapperBase};
  ${({ cartLayout, theme }) =>
    cartLayout && `padding: 4px 24px; border-radius: ${theme.borderRadius}px;`}
  ${validFor.desktop` 
    ${({ cartLayout }) => cartLayout && 'padding: 4px 20px;'}
	`};
  background-color: ${(props) =>
    transparentize(0.8, props.theme.colors.accentSecondary)};
`

const AccentWrapper = styled(Flex)`
  align-items: center;
`

const StyledFlex = styled(Flex)`
  flex-direction: row;
`

const StyledTiny = styled(Tiny)`
  line-height: 1.2;
`
const StyledReturnTitle = styled(Capitalized)`
  font-weight: 400;
  margin: 4px 0 4px 18px;
`
